import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Button, IconButton } from "@mui/material";
import SearchBar from "../../components/SearchBar";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import IosSwitchMain from "../../components/iosSwitchMain";
import FilterMenu from "../../components/filterMenu";
import { isValidInput } from "../../utils/validations";
import {
  useChangeEmployeeStatusMutation,
  useLazyGetEmployeesQuery,
} from "../../service/employee";
import { showError, showToast } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import { Members } from "../../types/General";
import CustomPagination from "../../components/CustomPagination";

const ManageEmployee = () => {
  const navigate = useNavigate();
  const [getEmployee] = useLazyGetEmployeesQuery();
  const [changeStatus] = useChangeEmployeeStatusMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [employees, setEmployees] = useState<Members[]>([]);
  const [count, setCount] = useState(10);
  const [page, setPage] = useState(1);

  const columns: GridColDef[] = [
    {
      flex: 1,
      minWidth: 140,
      field: "name",
      headerName: "Name",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() => navigate(`/manage-employee/view/${row?.id}`)}
          >
            <figure>
              <img
                src={
                  row?.image
                    ? row?.image
                    : "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.name}</b>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 170,
      field: "email",
      headerName: "Email",
      editable: false,
      sortable: false,
    },
    {
      flex: 1,
      minWidth: 140,
      field: "phone",
      headerName: "Phone No.",
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <div
            className="user_block click_action"
            onClick={() => navigate("/manage-employee/view")}
          >
            <p>
              <b>{`${row?.phoneNumber?.countryCode || ""} ${row?.phoneNumber?.phone || "-"}`}</b>
            </p>
          </div>
        );
      },
    },

    {
      field: "action",
      headerName: "Action",
      minWidth: 130,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <IconButton className="toggle_btn">
              {" "}
              <IosSwitchMain
                checked={row?.status === "Approved" ? true : false}
                onChange={() =>
                  handleStatusChange(
                    row?.id,
                    row?.status === "Approved" ? "Inactive" : "Approved"
                  )
                }
              />{" "}
            </IconButton>
            <IconButton
              className="edit_icon"
              onClick={() => navigate(`/manage-employee/edit/${row?.id}`)}
            >
              {" "}
              <img src="/static/images/edit_icon.svg" alt="icon" />{" "}
            </IconButton>
          </div>
        );
      },
    },
  ];

  const handleStatusChange = async (id: string, status: any) => {
    const body = {
      userId: id,
      userStatus: status,
    };

    try {
      const response = await changeStatus(body).unwrap();
      if (response?.status === 200) {
        const updatedData = employees?.map((item: any) => {
          if (item?.id === id) {
            return {
              ...item,
              status: status,
            };
          }
          return item;
        });
        setEmployees(updatedData);
        if (status === "Inactive") {
          showToast("Blocked successfully");
        } else {
          showToast("Unblocked successfully");
        }
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      console.error(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getEmployeesApi = async () => {
    setLoading(true);
    try {
      const response = await getEmployee({
        search: debouncedSearchTerm.trim(),
        page,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setEmployees(response?.data?.items || []);
        setCount(response?.data?.totalCount || 0);
      } else {
        setEmployees([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getEmployeesApi();
  }, [page, debouncedSearchTerm]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title d_flex">
        <h1>Employee Management</h1>

        <div className="right_s">
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search...."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm("")}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
          <Button
            className="btn btn_primary"
            onClick={() => navigate("/manage-employee/add")}
          >
            + Add New Employee
          </Button>
          <FilterMenu
            startDate={null}
            endDate={null}
            setStartDate={() => {}}
            setEndDate={() => {}}
          />
        </div>
      </div>

      <div className="table_wraper">
        <div className="sorting_table">
          <TableComponent columns={columns} rows={employees} />

          {employees?.length ? (
            <CustomPagination
              currentPage={page - 1}
              totalPages={Math.ceil(count / 10)}
              count={count}
              pageSize={10}
              onPageChange={(page) => setPage(page + 1)}
            />
          ) : null}
        </div>
      </div>
    </MainContainer>
  );
};

export default ManageEmployee;
